import {
  format,
  isSameYear,
  fromUnixTime,
  formatDistanceToNow,
  differenceInDays,
} from 'date-fns';
import { enUS, ptBR, es } from 'date-fns/locale';

const LOCALE_MAP = { pt_BR: ptBR, pt: ptBR, es };
let activeDateFnsLocale = enUS;

const SHORT_TIME_LABELS = {
  en: { now: 'now', m: 'm', h: 'h', d: 'd', mo: 'mo', y: 'y' },
  pt_BR: { now: 'agora', m: 'm', h: 'h', d: 'd', mo: 'mes', y: 'a' },
  pt: { now: 'agora', m: 'm', h: 'h', d: 'd', mo: 'mes', y: 'a' },
  es: { now: 'ahora', m: 'm', h: 'h', d: 'd', mo: 'mes', y: 'a' },
};
let activeShortLabels = SHORT_TIME_LABELS.en;

/**
 * Sets the date-fns locale used by every time helper.
 * Called on app boot and whenever the UI language changes.
 * @param {string} localeCode - vue-i18n locale code (e.g. 'pt_BR', 'es', 'en').
 */
export const setDateFnsLocale = localeCode => {
  activeDateFnsLocale = LOCALE_MAP[localeCode] ?? enUS;
  activeShortLabels = SHORT_TIME_LABELS[localeCode] ?? SHORT_TIME_LABELS.en;
};

/**
 * Formats a Unix timestamp into a human-readable time format.
 * @param {number} time - Unix timestamp.
 * @param {string} [dateFormat='HH:mm'] - Desired format of the time.
 * @returns {string} Formatted time string.
 */
export const messageStamp = (time, dateFormat = 'HH:mm') => {
  const unixTime = fromUnixTime(time);
  return format(unixTime, dateFormat, { locale: activeDateFnsLocale });
};

/**
 * Provides a formatted timestamp, adjusting the format based on the current year.
 * @param {number} time - Unix timestamp.
 * @param {string} [dateFormat='MMM d, yyyy'] - Desired date format.
 * @returns {string} Formatted date string.
 */
export const messageTimestamp = (time, dateFormat = 'MMM d, yyyy') => {
  const messageTime = fromUnixTime(time);
  const now = new Date();
  if (!isSameYear(messageTime, now)) {
    return format(messageTime, 'LLL d y, HH:mm', {
      locale: activeDateFnsLocale,
    });
  }
  return format(messageTime, dateFormat, { locale: activeDateFnsLocale });
};

/**
 * Converts a Unix timestamp to a relative time string (e.g., 3 hours ago).
 * @param {number} time - Unix timestamp.
 * @returns {string} Relative time string.
 */
export const dynamicTime = time => {
  const unixTime = fromUnixTime(time);
  return formatDistanceToNow(unixTime, {
    addSuffix: true,
    locale: activeDateFnsLocale,
  });
};

/**
 * Formats a Unix timestamp into a specified date format.
 * @param {number} time - Unix timestamp.
 * @param {string} [dateFormat='MMM d, yyyy'] - Desired date format.
 * @returns {string} Formatted date string.
 */
export const dateFormat = (time, df = 'MMM d, yyyy') => {
  const unixTime = fromUnixTime(time);
  return format(unixTime, df);
};

/**
 * Converts a detailed time description into a shorter format, optionally appending 'ago'.
 * @param {string} time - Detailed time description (e.g., 'a minute ago').
 * @param {boolean} [withAgo=false] - Whether to append 'ago' to the result.
 * @returns {string} Shortened time description.
 */
export const shortTimestamp = (time, withAgo = false) => {
  const suffix = withAgo ? ' ago' : '';

  // Legacy path: callers still passing an English relative-time string.
  if (typeof time === 'string') {
    const timeMappings = {
      'less than a minute ago': 'now',
      'in less than a minute': 'now',
      'a minute ago': `1m${suffix}`,
      'an hour ago': `1h${suffix}`,
      'a day ago': `1d${suffix}`,
      'a month ago': `1mo${suffix}`,
      'a year ago': `1y${suffix}`,
    };
    if (timeMappings[time]) {
      return timeMappings[time];
    }
    return time
      .replace(/about|over|almost|/g, '')
      .replace(/ minutes? ago/, `m${suffix}`)
      .replace(/ hours? ago/, `h${suffix}`)
      .replace(/ days? ago/, `d${suffix}`)
      .replace(/ months? ago/, `mo${suffix}`)
      .replace(/ years? ago/, `y${suffix}`);
  }

  // Preferred path: Unix timestamp (seconds) — locale-aware.
  const l = activeShortLabels;
  const diffSeconds = Math.floor(Date.now() / 1000) - time;
  if (diffSeconds < 60) return l.now;
  if (diffSeconds < 3600)
    return `${Math.floor(diffSeconds / 60)}${l.m}${suffix}`;
  if (diffSeconds < 86400)
    return `${Math.floor(diffSeconds / 3600)}${l.h}${suffix}`;
  if (diffSeconds < 2592000)
    return `${Math.floor(diffSeconds / 86400)}${l.d}${suffix}`;
  if (diffSeconds < 31536000)
    return `${Math.floor(diffSeconds / 2592000)}${l.mo}${suffix}`;
  return `${Math.floor(diffSeconds / 31536000)}${l.y}${suffix}`;
};

/**
 * Calculates the difference in days between now and a given timestamp.
 * @param {Date} now - Current date/time.
 * @param {number} timestampInSeconds - Unix timestamp in seconds.
 * @returns {number} Number of days difference.
 */
export const getDayDifferenceFromNow = (now, timestampInSeconds) => {
  const date = new Date(timestampInSeconds * 1000);
  return differenceInDays(now, date);
};

/**
 * Checks if more than 24 hours have passed since a given timestamp.
 * Useful for determining if retry/refresh actions should be disabled.
 * @param {number} timestamp - Unix timestamp.
 * @returns {boolean} True if more than 24 hours have passed.
 */
export const hasOneDayPassed = timestamp => {
  if (!timestamp) return true; // Defensive check
  return getDayDifferenceFromNow(new Date(), timestamp) >= 1;
};
